import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { DebugProvider } from "@/contexts/DebugContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { FeatureFlagGuard } from "./components/FeatureFlagGuard";
import { NavigationTracker } from "./components/NavigationTracker";

// Eager-load the landing page for fastest initial render
import Index from "./pages/Index";

// Lazy-load all other pages to reduce initial bundle size
const Orders = lazy(() => import("./pages/Orders"));
const Units = lazy(() => import("./pages/Units"));
const UnitDetails = lazy(() => import("./pages/UnitDetails"));
const UnitParts = lazy(() => import("./pages/UnitParts"));
const UnitOptions = lazy(() => import("./pages/UnitOptions"));
const Addresses = lazy(() => import("./pages/Addresses"));
const Parts = lazy(() => import("./pages/Parts"));
const ProductsView = lazy(() => import("./pages/ProductsView"));
const ContactInfo = lazy(() => import("./pages/ContactInfo"));
const MyFavorites = lazy(() => import("./pages/MyFavorites"));
const MyLogin = lazy(() => import("./pages/MyLogin"));
const MessageBoard = lazy(() => import("./pages/MessageBoard"));
const CaseManagement = lazy(() => import("./pages/CaseManagement"));
const MySpiffs = lazy(() => import("./pages/MySpiffs"));
const MySpiffDetail = lazy(() => import("./pages/MySpiffDetail"));
const SalesPersonSpiffs = lazy(() => import("./pages/SalesPersonSpiffs"));
const DealerSpiffs = lazy(() => import("./pages/DealerSpiffs"));
const SpiffPrintView = lazy(() => import("./pages/SpiffPrintView"));
const ViewDealerInvoices = lazy(() => import("./pages/ViewDealerInvoices"));
const WarrantyRegistration = lazy(() => import("./pages/WarrantyRegistration"));
const UnitRegistration = lazy(() => import("./pages/UnitRegistration"));
const UnitRegistrationConfirmation = lazy(() => import("./pages/UnitRegistrationConfirmation"));
const ServiceCampaigns = lazy(() => import("./pages/ServiceCampaigns"));
const ServiceCampaignDetail = lazy(() => import("./pages/ServiceCampaignDetail"));
const RGAList = lazy(() => import("./pages/RGAList"));
const CreateRGA = lazy(() => import("./pages/CreateRGA"));
const DealerPreAuthorizations = lazy(() => import("./pages/DealerPreAuthorizations"));
const NewPreAuthorization = lazy(() => import("./pages/NewPreAuthorization"));
const DealerClaims = lazy(() => import("./pages/DealerClaims"));
const ClaimDetail = lazy(() => import("./pages/ClaimDetail"));
const NewClaim = lazy(() => import("./pages/NewClaim"));
const YourTickets = lazy(() => import("./pages/YourTickets"));
const QuickOrder = lazy(() => import("./pages/QuickOrder"));
const InventoryTransfers = lazy(() => import("./pages/InventoryTransfers"));
const UnknownPartRequest = lazy(() => import("./pages/UnknownPartRequest"));
const ProductGroups = lazy(() => import("./pages/ProductGroups"));
const SavedCarts = lazy(() => import("./pages/SavedCarts"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const DigitalAssets = lazy(() => import("./pages/DigitalAssets"));
const DigitalAssetDetail = lazy(() => import("./pages/DigitalAssetDetail"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const PartsSearch = lazy(() => import("./pages/PartsSearch"));
const Admin = lazy(() => import("./pages/Admin"));
const LaborOperationsLibrary = lazy(() => import("./pages/LaborOperationsLibrary"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Service = lazy(() => import("./pages/Service"));
const Documents = lazy(() => import("./pages/Documents"));
const Profile = lazy(() => import("./pages/Profile"));
const AskMyRV = lazy(() => import("./pages/AskMyRV"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <FeatureFlagsProvider>
          <DebugProvider>
            <TooltipProvider>
            <Sonner />
            <BrowserRouter>
              <NavigationTracker />
              <ProtectedRoute>
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/units" element={<Units />} />
            <Route path="/units/:vin" element={<UnitDetails />} />
            <Route path="/vins" element={<Navigate to="/units" />} />
            <Route path="/vins/:vin" element={<Navigate to="/units" />} />
            <Route path="/unit-parts" element={<UnitParts />} />
            <Route path="/unit-options" element={<UnitOptions />} />
            <Route path="/vin-parts" element={<Navigate to="/unit-parts" />} />
            <Route path="/vin-options" element={<Navigate to="/unit-options" />} />
            <Route path="/addresses" element={<Addresses />} />
            <Route path="/parts" element={<Parts />} />
            <Route path="/parts/search" element={
              <FeatureFlagGuard flag="advancedProductSearch">
                <PartsSearch />
              </FeatureFlagGuard>
            } />
            <Route path="/parts/*" element={<ProductsView />} />
            <Route path="/products/:productId" element={<ProductDetail />} />
            <Route path="/contact-info" element={<ContactInfo />} />
            <Route path="/my-favorites" element={<MyFavorites />} />
            <Route path="/my-login" element={<MyLogin />} />
            <Route path="/message-board" element={<MessageBoard />} />
            <Route path="/case-management" element={<CaseManagement />} />
            <Route path="/my-spiffs" element={<MySpiffs />} />
            <Route path="/my-spiffs/:id" element={<MySpiffDetail />} />
            <Route path="/my-spiffs/:id/print" element={<SpiffPrintView />} />
            <Route path="/claimed-dealer-spiffs/:id/print" element={<SpiffPrintView />} />
            <Route path="/salesperson-spiffs" element={<SalesPersonSpiffs />} />
            <Route path="/dealer-spiffs" element={<DealerSpiffs />} />
            <Route path="/dealer-invoices" element={<ViewDealerInvoices />} />
            <Route path="/warranty-registration" element={<WarrantyRegistration />} />
            <Route path="/unit-registration" element={<UnitRegistration />} />
            <Route path="/unit-registration/confirmation" element={<UnitRegistrationConfirmation />} />
            <Route path="/service-campaigns" element={<ServiceCampaigns />} />
            <Route path="/service-campaigns/:campaignId" element={<ServiceCampaignDetail />} />
            <Route path="/rga-list" element={<RGAList />} />
            <Route path="/create-rga" element={<CreateRGA />} />
            <Route path="/dealer-pre-authorizations" element={<DealerPreAuthorizations />} />
            <Route path="/new-pre-authorization" element={<NewPreAuthorization />} />
            <Route path="/dealer-claims" element={<DealerClaims />} />
            <Route path="/dealer-claims/:claimId" element={<ClaimDetail />} />
            <Route path="/new-claim" element={<NewClaim />} />
            <Route path="/your-tickets" element={<YourTickets />} />
            <Route path="/quick-order" element={<QuickOrder />} />
            <Route path="/inventory-transfers" element={<InventoryTransfers />} />
            <Route path="/unknown-part-request" element={<UnknownPartRequest />} />
            <Route path="/product-groups" element={<ProductGroups />} />
          <Route path="/saved-carts" element={<SavedCarts />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
            <Route path="/digital-assets" element={<DigitalAssets />} />
            <Route path="/digital-assets/:assetId" element={<DigitalAssetDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/labor-operations" element={<LaborOperationsLibrary />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="/service" element={<Service />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/ask-my-rv" element={<AskMyRV />} />
            <Route path="/profile" element={<Profile />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </ProtectedRoute>
      </BrowserRouter>
      </TooltipProvider>
          </DebugProvider>
          </FeatureFlagsProvider>
  </CartProvider>
</AuthProvider>
</QueryClientProvider>
);

export default App;
